<template>
    <div class="faq-container">
        <!-- FAQ List View -->
        <div v-if="currentView === 'list'" class="faq-list">
            <div
                v-for="faq in faqs"
                :key="faq.id"
                class="faq-item"
                @click="viewFaqDetail(faq)"
                @keydown.enter="viewFaqDetail(faq)"
                @keydown.space.prevent="viewFaqDetail(faq)"
                tabindex="0"
            >
                <div class="faq-item-content">
                    <h5 class="faq-title">{{ faq.question }}</h5>
                    <div class="faq-arrow">
                        <i class="fas fa-chevron-right"></i>
                    </div>
                </div>
            </div>

            <div v-if="faqs.length === 0" class="no-faqs">
                <i class="fas fa-question-circle"></i>
                <p>No FAQs available</p>
            </div>
        </div>

        <!-- FAQ Detail View -->
        <div v-if="currentView === 'detail'" class="faq-detail">
            <!-- Back Button - only show when showBackButton is true -->
            <div v-if="showBackButton" class="faq-detail-header">
                <button
                    class="btn btn-link faq-back-btn"
                    @click="backToList"
                    type="button"
                >
                    <i class="fas fa-arrow-left"></i> Back to FAQs
                </button>
            </div>
            <div class="faq-detail-body">
                <h3 class="detail-question">{{ selectedFaq.question }}</h3>
                <div class="detail-answer" v-html="selectedFaq.answer"></div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'FaqsList',

    props: {
        faqs: {
            type: Array,
            required: true,
            default: () => []
        },
        idExtension: {
            type: String,
            default: ''
        },
        showMetadata: {
            type: Boolean,
            default: false
        },
        showBackButton: {
            type: Boolean,
            default: true
        }
    },

    data() {
        return {
            currentView: 'list', // 'list' or 'detail'
            selectedFaq: null
        }
    },

    methods: {
        viewFaqDetail(faq) {
            this.selectedFaq = faq
            this.currentView = 'detail'
            // Always emit the faq-opened event for parent components to handle
            this.$emit('faq-opened', faq)
        },

        backToList() {
            this.currentView = 'list'
            this.selectedFaq = null
            // Always emit the faq-closed event for parent components to handle
            this.$emit('faq-closed')
        },

        handleEscapeKey(event) {
            if (event.key === 'Escape' && this.currentView === 'detail') {
                this.backToList()
            }
        }
    },

    mounted() {
        document.addEventListener('keydown', this.handleEscapeKey)
    },

    beforeDestroy() {
        document.removeEventListener('keydown', this.handleEscapeKey)
    },

    watch: {
        faqs() {
            // Reset to list view when FAQs change (e.g., after search)
            this.currentView = 'list'
            this.selectedFaq = null
        }
    }
}
</script>

<style scoped>
.faq-container {
    z-index: 1000;
}

/* FAQ List Styles */
.faq-list {
    height: 100%;
    overflow-y: auto;
}

.faq-item {
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    margin-bottom: 8px;
    background: white;
    cursor: pointer;
    transition: all 0.2s ease;
}

.faq-item:hover {
    border-color: #007bff;
    box-shadow: 0 2px 8px rgba(0, 123, 255, 0.1);
    transform: translateY(-1px);
}

.faq-item:focus {
    outline: 2px solid #007bff;
    outline-offset: 2px;
}

.faq-item-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
}

.faq-title {
    margin: 0;
    font-size: 16px;
    font-weight: 500;
    color: #333;
    flex: 1;
    line-height: 1.4;
}

.faq-arrow {
    color: #999;
    font-size: 14px;
    margin-left: 12px;
    transition: all 0.2s ease;
}

.faq-item:hover .faq-arrow {
    color: #007bff;
    transform: translateX(3px);
}

.no-faqs {
    text-align: center;
    padding: 40px 20px;
    color: #666;
}

.no-faqs i {
    font-size: 48px;
    margin-bottom: 16px;
    opacity: 0.5;
}

.no-faqs p {
    font-size: 16px;
    margin: 0;
}

/* FAQ Detail Styles */
.faq-detail {
    height: 100%;
    display: flex;
    flex-direction: column;
    background: white;
    position: relative;
}

.faq-detail-header {
    padding: 12px 0;
    border-bottom: 1px solid #e0e0e0;
    flex-shrink: 0;
}

.faq-back-btn {
    color: #6c757d !important;
    font-size: 0.9rem;
    padding: 0.375rem 0;
    text-decoration: none;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    border: none;
    background: none;
}

.faq-back-btn::after {
    display: none !important;
    content: none !important;
}

.faq-back-btn:hover {
    color: #007bff !important;
    text-decoration: none;
}

.faq-back-btn:focus {
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
    outline: none;
}

.faq-back-btn i {
    font-size: 0.8rem;
}

.faq-detail-body {
    flex: 1;
    padding: 12px 0px;
    padding-bottom: 20px;
    overflow-y: auto;
}

.detail-question {
    margin: 0 0 20px 0;
    font-size: 20px;
    font-weight: 600;
    color: #333;
    line-height: 1.3;
}

.detail-answer {
    color: #555;
    line-height: 1.6;
    font-size: 15px;
}

/* Style HTML content in answers */
.detail-answer :deep(p) {
    margin: 0 0 16px 0;
}

.detail-answer :deep(p:last-child) {
    margin-bottom: 0;
}

.detail-answer :deep(ul),
.detail-answer :deep(ol) {
    margin: 16px 0;
    padding-left: 24px;
}

.detail-answer :deep(li) {
    margin-bottom: 8px;
}

.detail-answer :deep(strong),
.detail-answer :deep(b) {
    font-weight: 600;
    color: #333;
}

.detail-answer :deep(a) {
    color: #007bff;
    text-decoration: none;
}

.detail-answer :deep(a:hover) {
    text-decoration: underline;
}

.detail-answer :deep(h1),
.detail-answer :deep(h2),
.detail-answer :deep(h3),
.detail-answer :deep(h4),
.detail-answer :deep(h5),
.detail-answer :deep(h6) {
    margin: 24px 0 16px 0;
    font-weight: 600;
    color: #333;
}

.detail-answer :deep(h1:first-child),
.detail-answer :deep(h2:first-child),
.detail-answer :deep(h3:first-child),
.detail-answer :deep(h4:first-child),
.detail-answer :deep(h5:first-child),
.detail-answer :deep(h6:first-child) {
    margin-top: 0;
}

/* Responsive Design */
@media (max-width: 768px) {
    .faq-item-content {
        padding: 14px 16px;
    }

    .faq-title {
        font-size: 15px;
    }

    .faq-detail-header {
        padding: 10px 16px;
    }

    .faq-detail-body {
        padding: 12px 0px;
        padding-bottom: 100px;
    }

    .detail-question {
        font-size: 18px;
        margin-bottom: 16px;
    }

    .detail-answer {
        font-size: 14px;
    }
}

@media (max-width: 480px) {
    .faq-item-content {
        padding: 12px 14px;
    }

    .faq-title {
        font-size: 14px;
    }

    .faq-detail-body {
        padding: 12px 0px;
    }

    .detail-question {
        font-size: 16px;
    }

    .btn-back {
        font-size: 13px;
    }
}

/* Animation for reduced motion users */
@media (prefers-reduced-motion: reduce) {
    .faq-item,
    .faq-arrow,
    .btn-back {
        transition: none;
    }
}

/* High contrast mode */
@media (prefers-contrast: high) {
    .faq-item {
        border-width: 2px;
        border-color: #000;
    }

    .faq-item:hover {
        border-color: #0066cc;
        background-color: #f0f8ff;
    }

    .btn-back {
        font-weight: 600;
    }
}
</style>



// WEBPACK FOOTER //
// src/Assets/components/Faqs/List.vue